import type React from "react";
import { useState } from "react";

import { Link, useLocation } from "@tanstack/react-router";
import {
  ChevronDown,
  ChevronRight,
  ClipboardList,
  FileText,
  Users,
} from "lucide-react";

import { cn } from "@pbz/ui-kit";

import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import type { MenuItem } from "@/types/permission";

interface SidebarProps {
  customClasses?: string;
}

const Sidebar: React.FC<SidebarProps> = ({ customClasses }) => {
  const location = useLocation();
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const { canListUsers, canCreateLead, canListLeads } = usePermissionCheck();
  const menuItems: MenuItem[] = [
    {
      key: "USER_MANAGEMENT",
      icon: Users,
      label: "User Management",
      href: "/users",
      hasPermission: canListUsers,
    },
    {
      key: "LEAD_CREATION",
      icon: FileText,
      label: "Lead Creation",
      href: "/lead/create",
      hasPermission: canCreateLead,
    },
    {
      key: "LEAD_MANAGEMENT",
      icon: ClipboardList,
      label: "Lead Management",
      href: "/lead/list",
      hasPermission: canListLeads,
    },
  ];

  const isActive = (href?: string): boolean => {
    if (!href) {
      return false;
    }
    return location.pathname === href || location.pathname.startsWith(href);
  };

  const hasSubmenu = (item: MenuItem): boolean => {
    return !!(item.submenuItems && item.submenuItems.length > 0);
  };

  // Check if any submenu item is active
  const hasActiveSubmenuItem = (item: MenuItem): boolean => {
    if (!item.submenuItems) {
      return false;
    }
    return item.submenuItems.some((subItem) => isActive(subItem.href));
  };

  const isExpanded = (key: string): boolean => {
    // First check if manually expanded
    if (expandedKey === key) {
      return true;
    }

    // Then check if this menu has an active submenu item
    const menuItem = menuItems.find((item) => item.key === key);
    if (menuItem && hasActiveSubmenuItem(menuItem)) {
      return true;
    }

    return false;
  };

  const handleItemClick = (item: MenuItem) => {
    if (hasSubmenu(item)) {
      // Toggle expansion - only one can be expanded at once
      setExpandedKey(isExpanded(item.key) ? null : item.key);
    }
    // If item has href, navigation will be handled by Link component
  };

  // Recursive function to render menu items
  const renderMenuItem = (item: MenuItem, depth = 0): React.ReactNode => {
    const itemHasSubmenu = hasSubmenu(item);
    const itemIsActive = isActive(item.href);
    const itemIsExpanded = isExpanded(item.key);

    // Base styles for menu items
    const baseClasses = cn(
      "flex items-center gap-3 px-4 py-2.5 font-medium text-sm transition-colors",
      depth > 0 ? "ml-4" : "", // Indent submenu items
      itemIsActive
        ? "border-blue-700 border-r-2 bg-blue-50 text-blue-700"
        : "text-gray-700 hover:bg-gray-50",
    );

    const content = (
      <>
        <div className="flex items-center gap-3">
          <item.icon className="h-5 w-5" />
          <span>{item.label}</span>
        </div>
        {itemHasSubmenu && (
          <div className="ml-auto">
            {itemIsExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </div>
        )}
      </>
    );

    return (
      <li key={item.key}>
        {/* Render as Link if item has href, otherwise as button */}
        {item.href ? (
          <Link
            className={cn(baseClasses, "justify-between")}
            // onClick={() => handleItemClick(item)}
            to={item.href}
          >
            {content}
          </Link>
        ) : (
          <button
            className={cn(baseClasses, "w-full justify-between text-left")}
            onClick={() => handleItemClick(item)}
            type="button"
          >
            {content}
          </button>
        )}

        {/* Render submenu items recursively */}
        {itemHasSubmenu && itemIsExpanded && (
          <ul className="mt-1 space-y-1">
            {item.submenuItems?.map((subItem) =>
              renderMenuItem(subItem, depth + 1),
            )}
          </ul>
        )}
      </li>
    );
  };

  // Then filter sidebar items based on user permissions:
  const filteredSidebarItems = menuItems.filter((item: MenuItem) =>
    item.hasPermission(),
  );

  return (
    <aside
      className={cn(
        "h-full w-64 border-gray-200 border-r bg-white",
        customClasses,
      )}
    >
      {/* Logo */}
      <div className="flex items-center justify-center">
        <img
          alt="PaisabazaarLogo"
          className="my-5"
          src="https://cdn.paisabazaar.ae/common/images/logo.svg"
        />
      </div>

      {/* Navigation Menu */}
      <nav className="py-4">
        <ul className="space-y-1">
          {filteredSidebarItems.map((item: MenuItem) => renderMenuItem(item))}
        </ul>
      </nav>
    </aside>
  );
};

export default Sidebar;
